
import { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import EmailList from "@/components/email/EmailList";
import EmailViewer from "@/components/email/EmailViewer";
import AIResponseGenerator from "@/components/ai/AIResponseGenerator";
import InboxSelector from "@/components/email/InboxSelector";
import AssistantSelector from "@/components/ai/AssistantSelector";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { MainHeader } from "@/components/layout/MainHeader";
import { BulkResponseGenerator } from "@/components/email/BulkResponseGenerator";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Link } from "react-router-dom";
import { Email } from "@/types/email";

const Index = () => {
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<Email[]>([]);
  const [generatedResponses, setGeneratedResponses] = useState<Map<string, string>>(new Map());
  const [selectedInbox, setSelectedInbox] = useState("");
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [isGeneratingMultiple, setIsGeneratingMultiple] = useState(false);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['emailAccounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_accounts')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const handleMultipleSelection = useCallback((emails: Email[]) => {
    setSelectedEmails(emails);
  }, []);

  const handleResponseGenerated = useCallback((emailId: string, response: string) => {
    setGeneratedResponses(prev => new Map(prev).set(emailId, response));
  }, []);

  if (!isLoading && accounts.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <MainHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-6 text-center space-y-4">
            <h2 className="text-xl font-semibold">Welcome to Maia</h2>
            <p className="text-muted-foreground">
              No email accounts connected. Please add an email account in the Settings page to get started.
            </p>
          </Card>
        </div>
        <footer className="fixed bottom-0 left-0 p-4 text-sm text-muted-foreground">
          <div className="space-x-4">
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainHeader />
      <main className="flex-1 p-4">
        <div className="max-w-[2000px] mx-auto w-full grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-2 space-y-4">
            <Card className="p-4 border">
              <InboxSelector 
                selectedInbox={selectedInbox}
                onSelectInbox={setSelectedInbox}
              />
            </Card>
            <Card className="p-4 border">
              <AssistantSelector
                selectedAssistant={selectedAssistant}
                onSelectAssistant={setSelectedAssistant}
              />
            </Card>
          </div>
          
          <ResizablePanelGroup direction="horizontal" className="col-span-12 lg:col-span-10">
            <ResizablePanel defaultSize={30} minSize={20} maxSize={50}>
              <Card className="h-full border">
                <EmailList 
                  onSelectEmail={setSelectedEmail} 
                  onSelectMultiple={handleMultipleSelection}
                  selectedInbox={selectedInbox}
                />
              </Card>
            </ResizablePanel>
            
            <ResizableHandle />
            
            <ResizablePanel defaultSize={70}>
              <div className="space-y-4">
                {selectedEmails.length > 1 && (
                  <BulkResponseGenerator 
                    selectedEmails={selectedEmails}
                    onResponseGenerated={handleResponseGenerated}
                    isGeneratingMultiple={isGeneratingMultiple}
                    setIsGeneratingMultiple={setIsGeneratingMultiple}
                    selectedAssistant={selectedAssistant}
                  />
                )}
                
                <div className="space-y-4">
                  {selectedEmails.length > 1 ? (
                    selectedEmails.map(email => (
                      <Card key={email.id} className="p-4 border">
                        <AIResponseGenerator 
                          email={email} 
                          selectedAssistant={selectedAssistant}
                        />
                      </Card>
                    ))
                  ) : (
                    selectedEmail && (
                      <Card className="p-4 border">
                        <AIResponseGenerator 
                          email={selectedEmail} 
                          selectedAssistant={selectedAssistant}
                        />
                      </Card>
                    )
                  )}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </main>
      <footer className="fixed bottom-0 left-0 p-4 text-sm text-muted-foreground">
        <div className="space-x-4">
          <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Index;
